import sanitize from 'uparser';
import domdiff from 'udomdiff';
import { isObject } from '@kirei/shared';

const IE = document.importNode.length != 1;
const filter = NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT;
const PERSIST_NODE_TYPE = 123;
const createWalker = IE ? (node) => document.createTreeWalker(node, filter, null, false) : (node) => document.createTreeWalker(node, filter);
function remove(node) {
  const {firstChild} = node;
  const parent = firstChild.parentNode;
  while (parent.firstChild != parent.lastChild) {
    parent.lastChild.remove();
  }
  return firstChild;
}
function clearNode(node) {
  if (!node.lastChild)
    return;
  node.textContent = "";
}
function diffable(node, operation) {
  if (node.nodeType === PERSIST_NODE_TYPE) {
    if (1 / operation < 0) {
      return operation ? remove(node) : node.lastChild;
    }
    return operation ? node.valueOf() : node.firstChild;
  }
  return node;
}
function persistent(frag) {
  const children = frag.childNodes;
  if (children.length < 2)
    return children[0];
  const nodes = Array.from(children);
  const nodeType = PERSIST_NODE_TYPE;
  function valueOf() {
    if (children.length !== nodes.length) {
      nodes.forEach((n) => frag.appendChild(n));
    }
    return frag;
  }
  return {nodeType, valueOf, firstChild: nodes[0]};
}
function diff(ref, oldNodes, newNodes) {
  return domdiff(ref.parentNode, oldNodes, newNodes, diffable, ref);
}
function createTemplate(svg, markup) {
  const template = document.createElement("template");
  if (svg) {
    template.innerHTML = `<svg>${markup}</svg>`;
    const {content} = template;
    const svg2 = content.firstChild;
    content.removeChild(svg2);
    while (svg2.firstChild) {
      content.appendChild(svg2.firstChild);
    }
  } else {
    template.innerHTML = markup;
  }
  return template;
}
class LRUWeakMap extends WeakMap {
  constructor(size) {
    super();
    this.list = [];
    if (size <= 0) {
      throw new TypeError(`LRU Cache max size has to be positive integer larger than 0, got '${size}'.`);
    }
    this.max = size;
  }
  delete(key) {
    const idx = this.list.indexOf(key);
    if (idx != -1) {
      this.list.splice(idx, 1);
    }
    return super.delete(key);
  }
  get(key) {
    const idx = this.list.lastIndexOf(key);
    if (idx < this.list.length - 1) {
      if (idx != -1) {
        this.list.splice(idx, 1);
      }
      this.list.push(key);
    }
    return super.get(key);
  }
  set(key, value) {
    const idx = this.list.indexOf(key);
    if (idx == -1) {
      if (this.max <= this.list.length) {
        const [tail] = this.list.splice(0, 1);
        super.delete(tail);
      }
    } else if (idx < this.list.length - 1) {
      this.list.splice(idx, 1);
    }
    this.list.push(key);
    return super.set(key, value);
  }
}

var PatchType;
(function(PatchType2) {
  PatchType2["NODE"] = "node";
  PatchType2["ATTR"] = "attr";
  PatchType2["TEXT"] = "text";
})(PatchType || (PatchType = {}));
function mapAttribute(attr, value) {
  if (!isObject(value)) {
    return value;
  } else if (Array.isArray(value)) {
    if (attr == "style") {
      throw new TypeError("Style attribute expressions cannot map array");
    }
    return value.filter((x) => x).join(" ");
  }
  const keys = Object.keys(value).filter((key) => value[key]);
  if (attr == "style") {
    return keys.map((key) => `${key}=${value[key]}`).join(";");
  }
  return keys.join(" ");
}
function propPatcher(node, name) {
  return (pending) => {
    node[name] = pending;
  };
}
function eventPatcher(node, name) {
  let listener;
  function boundListener(e) {
    return listener.call(node, e);
  }
  return (pending) => {
    if (listener !== pending) {
      if (pending == null) {
        node.removeEventListener(name, boundListener, false);
      } else if (listener == null) {
        node.addEventListener(name, boundListener, false);
      }
      listener = pending;
    }
  };
}
const defaultCompiler = {
  attr(node, attr) {
    const prefix = attr[0];
    if (prefix == ".") {
      return propPatcher(node, attr.slice(1));
    } else if (prefix == "@") {
      return eventPatcher(node, attr.slice(1));
    }
    const shouldMap = attr == "class" || attr == "style";
    let value;
    return (pending) => {
      if (value === pending)
        return;
      value = pending;
      if (value == null) {
        node.removeAttribute(attr);
      } else {
        node.setAttribute(attr, shouldMap ? mapAttribute(attr, value) : value);
      }
    };
  },
  node(ref) {
    let nodes = [];
    let value;
    let text;
    const nodeParser = (pending) => {
      if (pending == null) {
        if (value) {
          value = pending;
          nodes = diff(ref, nodes, []);
        }
      } else if (!isObject(pending)) {
        if (value !== pending) {
          value = pending;
          if (!text) {
            text = document.createTextNode(value);
          } else {
            text.textContent = value;
          }
          nodes = diff(ref, nodes, [text]);
        }
      } else if ("nodeType" in pending && pending !== value) {
        value = pending instanceof DocumentFragment ? Array.from(pending.childNodes) : [pending];
        nodes = diff(ref, nodes, value);
      } else if (Array.isArray(pending)) {
        value = pending;
        if (value.length === 0 || isObject(value[0])) {
          nodes = diff(ref, nodes, value);
        } else if (value !== pending) {
          nodeParser(String(pending));
        }
      } else {
        throw new TypeError("Invalid node expression in html template");
      }
    };
    return nodeParser;
  },
  text(node) {
    let value;
    return (pending) => {
      if (value !== pending) {
        value = pending;
        node.textContent = value == null ? "" : value;
      }
    };
  }
};

const TEXT_TAGS = ["style", "textarea"];
const PREFIX = "is\xB5";
const contentCache = new LRUWeakMap(500);
function createCache() {
  return {stack: [], instance: null, node: null};
}
function createPatch(node, type, attr) {
  const path = [];
  while (node.parentNode) {
    let i = 0;
    for (let n = node; n = n.previousSibling; i++)
      ;
    node = node.parentNode;
    path.push(i);
  }
  return {attr, type, path};
}
function compileContent(type, strings, scopeName) {
  var _a, _b;
  const isSvg = type === "svg";
  const template = createTemplate(isSvg, sanitize(strings, PREFIX, isSvg));
  const patches = [];
  const walker = createWalker(template.content);
  const len = strings.length - 1;
  let i = 0;
  let search = `${PREFIX}${i}`;
  const styles = template.querySelectorAll("style");
  if (styles.length) {
    const style = styles[0];
    template.insertBefore(style, template.firstChild);
    for (let i2 = 1; i2 < styles.length; i2++) {
      const el = styles[i2];
      (_a = el.parentNode) == null ? void 0 : _a.removeChild(el);
      style.textContent += el.textContent;
    }
  }
  while (i < len) {
    const node = walker.nextNode();
    if (!node)
      throw new Error("Parsing error");
    if (node.nodeType == Node.COMMENT_NODE) {
      if (node.textContent === search) {
        node.textContent = "";
        patches[i] = createPatch(node, PatchType.NODE);
        search = `${PREFIX}${++i}`;
      }
    } else {
      const el = node;
      let attr;
      while (attr = el.getAttribute(search)) {
        el.removeAttribute(search);
        patches[i] = createPatch(node, PatchType.ATTR, attr);
        search = `${PREFIX}${++i}`;
      }
      if (TEXT_TAGS.includes(el.localName) && node.textContent.trim() === `<!--${search}-->`) {
        node.textContent = "";
        patches[i] = createPatch(node, PatchType.TEXT);
        search = `${PREFIX}${++i}`;
      }
    }
  }
  if (scopeName && typeof window != "undefined") {
    (_b = window.ShadyCSS) == null ? void 0 : _b.prepareTemplate(template, scopeName);
  }
  return {node: template, patches};
}
class Template {
  static updateValues(cache, values, compiler) {
    var _a, _b;
    const {stack} = cache;
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      let cache2;
      if (value instanceof Template) {
        cache2 = (_a = stack[i]) != null ? _a : createCache();
        values[i] = value.update(cache2, compiler);
      } else if (Array.isArray(value)) {
        cache2 = (_b = stack[i]) != null ? _b : createCache();
        Template.updateValues(cache2, value, compiler);
      }
      stack[i] = cache2;
    }
    if (values.length < stack.length) {
      stack.splice(values.length);
    }
  }
  constructor(type, strings, values) {
    this.type = type;
    this.strings = strings;
    this.values = values;
  }
  updateOnce(compiler, scopeName) {
    return this.update(createCache(), compiler, scopeName);
  }
  update(cache, compiler, scopeName) {
    var _a;
    const {strings, type, values} = this;
    let {instance} = cache;
    if (!compiler) {
      compiler = defaultCompiler;
    }
    if (!instance || instance.strings !== strings || instance.type !== type) {
      instance = cache.instance = this.compile(compiler, scopeName);
    }
    const {patchers} = instance;
    Template.updateValues(cache, values, compiler);
    for (let i = 0; i < values.length; i++) {
      patchers[i](values[i]);
    }
    return (_a = cache.node) != null ? _a : cache.node = persistent(instance.root);
  }
  compile(compiler, scopeName) {
    const {strings, type} = this;
    let content = contentCache.get(strings);
    if (!content) {
      contentCache.set(strings, content = compileContent(type, strings, scopeName));
    }
    const {patches, node} = content;
    const root = document.importNode(node.content, true);
    const patchers = patches.map(({type: type2, attr, path}) => {
      var _a;
      const node2 = path.reduceRight((n, i) => n.childNodes[i], root);
      return ((_a = compiler == null ? void 0 : compiler[type2]) == null ? void 0 : _a.call(compiler, node2, attr)) || defaultCompiler[type2](node2, attr);
    });
    return {strings, type, patchers, root};
  }
}

const rendered = new WeakMap();
const {html, svg, render} = customize({});
function customize(opts) {
  const {compiler} = opts;
  return {
    html: createLiteral("html", opts),
    svg: createLiteral("svg", opts),
    render(template, root, renderOptions = {}) {
      const {scopeName, mount} = renderOptions;
      let cache = rendered.get(root);
      if (template != null) {
        if (!cache) {
          rendered.set(root, cache = createCache());
        }
        const wire = cache.node;
        let node;
        if (template instanceof Template) {
          node = template.update(cache, compiler, scopeName);
        } else if (template instanceof Node) {
          node = template;
        } else {
          throw new TypeError("Invalid render template, expected Template or Node");
        }
        if (!root.childNodes.length || wire !== node) {
          if (mount == null || mount) {
            clearNode(root);
            root.appendChild(node.valueOf());
          }
        }
        return node;
      }
      if (cache) {
        clearNode(root);
        cache.node = null;
      }
    }
  };
}
function createLiteral(type, opts) {
  const {compiler, literals} = opts;
  const keyed = new WeakMap();
  const template = (strings, ...values) => new Template(type, strings, values);
  template.key = (ref, key, template2) => {
    if (!template2) {
      template2 = key;
      key = void 0;
    }
    let memo = keyed.get(ref);
    if (!memo) {
      keyed.set(ref, memo = new Map());
    }
    let cache = memo.get(key);
    if (!cache) {
      memo.set(key, cache = createCache());
    }
    return template2.update(cache, compiler);
  };
  if (isObject(literals)) {
    for (let key of Object.keys(literals)) {
      if (key in template) {
        throw new TypeError("Cannot override properties in literals");
      }
      template[key] = literals[key];
    }
  }
  return template;
}

export { LRUWeakMap, Template, createCache, customize, defaultCompiler, html, render, svg };
//# sourceMappingURL=html.mjs.map
